import React from "react";
import GoldRule from "../../components/GoldRule";

/* ─── Shared layout for the facial feminization supporting pages ───
   The main page is /facial-feminization-surgery (src/pages/FacialFeminization.js). Each supporting page
   goes deeper on one part of it (forehead, hairline, jaw and chin, tracheal shave, recovery), links back
   to the main page, and links across to its siblings. They share this layout so they read as one family.

   Nothing in this file may read `window` or `document` while rendering, or the publish-time render breaks. */

export const C = { // site colors; the values live in src/theme.css
  navy: "var(--c-navy)",
  navyLight: "var(--c-navy-light)",
  gold: "var(--c-gold)",
  goldDark: "var(--c-gold-dark)",
  white: "var(--c-white)",
  bg: "var(--c-bg)",
  bgAlt: "var(--c-bg-alt)",
  textBody: "var(--c-text-body)",
  textMuted: "var(--c-text-muted)",
  textLight: "var(--c-text-light)",
  border: "var(--c-border)",
  transBlue: "var(--c-trans-blue)",
  transPink: "var(--c-trans-pink)",
};

export const FFS_HUB = { href: "/facial-feminization-surgery", label: "Facial feminization surgery" };
export const PHONE_DISPLAY = "(212) 222-4628";
export const PHONE_HREF = "tel:2122224628";
export const EMAIL = "info@ovadiaps.com";

/* Every supporting page, for the "Related" section. Each page filters itself out. */
export const FFS_SUBPAGES = [
  { href: "/forehead-contouring", label: "Forehead contouring and brow bone reduction", short: "Frontal sinus setback, orbital contouring, and the brow." },
  { href: "/hairline-lowering", label: "Hairline lowering", short: "Bringing the hairline forward through the hairline incision." },
  { href: "/jaw-and-chin-contouring", label: "Jaw and chin contouring", short: "Mandible contouring and genioplasty for the lower face." },
  { href: "/lip-lift", label: "Upper lip lift", short: "Shortening the upper lip so more of it shows; timing after rhinoplasty." },
  { href: "/fat-grafting", label: "Fat grafting", short: "Your own fat for the cheeks and other areas of the face; also the breast and the hips and buttocks." },
  { href: "/tracheal-shave", label: "Tracheal shave", short: "Reducing the Adam's apple, when it is part of the plan." },
  { href: "/ffs-recovery", label: "Recovery after FFS", short: "Swelling, the compression garment, sutures and staples, and the timeline." },
];

export const faqStructuredData = (faqs) => [
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
  },
];

export const STYLES = `
  :root { --head: 'Cormorant Garamond', serif; --body: 'Libre Franklin', sans-serif; }
  * { margin: 0; padding: 0; box-sizing: border-box; }
  html { scroll-behavior: smooth; }
  body { overflow-x: hidden; font-family: 'Libre Franklin', sans-serif; }
  .mbtn { display: none !important; }
  @media (max-width: 900px) {
    .dnav { display: none !important; }
    .mbtn { display: flex !important; }
    .fgrid { grid-template-columns: 1fr 1fr !important; }
  }
  @media (max-width: 600px) { .fgrid { grid-template-columns: 1fr !important; } }
  ::selection { background: ${C.gold}; color: ${C.white}; }
  *:focus-visible { outline: 2px solid ${C.gold}; outline-offset: 2px; }

  .fs { font-variant-numeric: lining-nums; font-feature-settings: "lnum" 1; }
  .fs section[id] { scroll-margin-top: 110px; }
  .fs-wrap { max-width: 1080px; margin: 0 auto; padding: 0 clamp(16px, 3vw, 24px); }
  .fs-narrow { max-width: 760px; }
  .fs-sec { padding: clamp(56px, 9vw, 96px) 0; }
  .fs-sec + .fs-sec { padding-top: 0; }
  .fs-sec.fs-alt { background: ${C.bgAlt}; padding-top: clamp(56px, 9vw, 96px); }
  .fs-sec.fs-alt + .fs-sec { padding-top: clamp(56px, 9vw, 96px); }
  .fs-eyebrow { font-family: var(--body); font-size: 13px; font-weight: 600; color: ${C.goldDark}; letter-spacing: 4px; text-transform: uppercase; margin-bottom: 10px; }
  .fs-h2 { font-family: var(--head); font-size: clamp(30px, 4.4vw, 44px); font-weight: 600; color: ${C.navy}; line-height: 1.15; }
  .fs-rule { width: 50px; height: 2px; background: ${C.gold}; margin: 18px 0 28px; }
  .fs-p { font-family: var(--body); font-size: 17px; line-height: 1.8; color: ${C.textBody}; margin-bottom: 18px; }
  .fs-p:last-child { margin-bottom: 0; }
  .fs-p a, .fs-note a, .fs-faq p a { color: ${C.navy}; font-weight: 600; text-decoration: underline; text-decoration-color: ${C.gold}; text-decoration-thickness: 1.5px; text-underline-offset: 4px; }

  .fs-crumb { font-family: var(--body); font-size: 13px; letter-spacing: 1px; text-transform: uppercase; color: rgba(255,255,255,0.7); margin-bottom: 22px; }
  .fs-crumb a { color: ${C.gold}; text-decoration: none; font-weight: 600; }
  .fs-crumb a:hover { text-decoration: underline; }

  .fs-hero { background: ${C.navy}; color: ${C.white}; padding: clamp(130px, 16vw, 170px) 0 0; }
  .fs-hero h1 { font-family: var(--head); font-size: clamp(38px, 6.4vw, 66px); font-weight: 500; line-height: 1.05; margin-bottom: 20px; }
  .fs-hero-lede { font-family: var(--body); font-size: clamp(17px, 2vw, 20px); line-height: 1.7; color: rgba(255,255,255,0.82); max-width: 680px; }
  .fs-stripe { display: flex; width: 96px; height: 3px; margin-bottom: 26px; }
  .fs-stripe span { flex: 1; }
  .fs-btns { display: flex; gap: 12px; flex-wrap: wrap; margin-top: 34px; }
  .fs-btn { font-family: var(--body); font-size: 14px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; text-decoration: none; padding: 15px 30px; display: inline-block; transition: background 0.25s, color 0.25s, border-color 0.25s; }
  .fs-btn-gold { background: ${C.gold}; color: ${C.white}; border: 2px solid ${C.gold}; }
  .fs-btn-gold:hover { background: ${C.goldDark}; border-color: ${C.goldDark}; }
  .fs-btn-line { background: transparent; color: ${C.white}; border: 2px solid rgba(255,255,255,0.4); }
  .fs-btn-line:hover { border-color: ${C.white}; }
  .fs-btn-navy { background: transparent; color: ${C.navy}; border: 2px solid ${C.navy}; }
  .fs-btn-navy:hover { background: ${C.navy}; color: ${C.white}; }
  .fs-facts { display: grid; grid-template-columns: repeat(4, 1fr); margin-top: clamp(48px, 7vw, 76px); border-top: 1px solid rgba(255,255,255,0.14); }
  .fs-facts.fs-facts-3 { grid-template-columns: repeat(3, 1fr); }
  .fs-fact { padding: 26px 20px 30px 0; }
  .fs-fact + .fs-fact { padding-left: 20px; border-left: 1px solid rgba(255,255,255,0.14); }
  .fs-fact b { display: block; font-family: var(--head); font-size: clamp(24px, 3vw, 32px); font-weight: 500; color: ${C.gold}; line-height: 1.1; }
  .fs-fact span { display: block; font-family: var(--body); font-size: 14px; color: rgba(255,255,255,0.72); margin-top: 6px; line-height: 1.5; }

  .fs-jump { background: ${C.white}; border-bottom: 1px solid ${C.border}; }
  .fs-jump-in { display: flex; gap: 4px; overflow-x: auto; scrollbar-width: none; }
  .fs-jump-in::-webkit-scrollbar { display: none; }
  .fs-jump a { font-family: var(--body); font-size: 13px; font-weight: 600; letter-spacing: 1.5px; text-transform: uppercase; color: ${C.navy}; text-decoration: none; padding: 18px 14px; white-space: nowrap; border-bottom: 2px solid transparent; }
  .fs-jump a:hover { border-bottom-color: ${C.gold}; }

  .fs-cards { display: grid; grid-template-columns: repeat(2, 1fr); gap: 20px; }
  .fs-cards.fs-cards-3 { grid-template-columns: repeat(3, 1fr); }
  .fs-card { position: relative; background: ${C.white}; border: 1px solid ${C.border}; border-top: 3px solid ${C.gold}; padding: clamp(22px, 3vw, 30px); transition: background 0.3s ease; }
  .fs-card:hover { background: #fffdf8; }
  .fs-card h3 { font-family: var(--head); font-size: 24px; font-weight: 600; color: ${C.navy}; line-height: 1.25; margin-bottom: 10px; }
  .fs-card .fs-plain { font-family: var(--body); font-size: 13px; font-weight: 600; letter-spacing: 2px; text-transform: uppercase; color: ${C.goldDark}; margin: -4px 0 12px; }
  .fs-card p { font-family: var(--body); font-size: 15.5px; line-height: 1.75; color: ${C.textMuted}; }
  .fs-card p + p { margin-top: 12px; }
  .fs-card.fs-wide { grid-column: 1 / -1; }
  .fs-card.fs-wide p { max-width: 820px; }

  .fs-list { list-style: none; margin-top: 16px; }
  .fs-list li { font-family: var(--body); font-size: 15px; line-height: 1.6; color: ${C.textBody}; padding: 9px 0 9px 22px; border-top: 1px solid ${C.border}; position: relative; }
  .fs-list li::before { content: ""; position: absolute; left: 2px; top: 18px; width: 8px; height: 2px; background: ${C.gold}; }
  .fs-note { font-family: var(--body); font-size: 15.5px; line-height: 1.75; color: ${C.textMuted}; margin-top: 26px; padding-left: 18px; border-left: 3px solid ${C.gold}; }
  .fs-fine { font-family: var(--body); font-size: 13.5px; line-height: 1.6; color: ${C.textLight}; }

  .fs-steps { counter-reset: step; margin-top: 8px; }
  .fs-step { display: grid; grid-template-columns: 56px 1fr; gap: 18px; padding: 22px 0; border-top: 1px solid ${C.border}; }
  .fs-step:last-child { border-bottom: 1px solid ${C.border}; }
  .fs-step::before { counter-increment: step; content: counter(step, decimal-leading-zero); font-family: var(--head); font-size: 30px; font-weight: 500; color: ${C.gold}; line-height: 1; padding-top: 2px; }
  .fs-step h3 { font-family: var(--head); font-size: 24px; font-weight: 600; color: ${C.navy}; line-height: 1.25; margin-bottom: 8px; }
  .fs-step p { font-family: var(--body); font-size: 15.5px; line-height: 1.75; color: ${C.textMuted}; }

  .fs-tl { display: grid; grid-template-columns: repeat(6, 1fr); margin-top: 44px; }
  .fs-tl-step { position: relative; padding: 28px 18px 0 0; border-top: 2px solid ${C.navy}; }
  .fs-tl-step::before { content: ""; position: absolute; top: -7px; left: 0; width: 12px; height: 12px; border-radius: 50%; background: ${C.gold}; border: 2px solid ${C.bg}; }
  .fs-tl-step b { display: block; font-family: var(--head); font-size: 23px; font-weight: 600; color: ${C.navy}; line-height: 1.15; margin-bottom: 6px; }
  .fs-tl-step span { font-family: var(--body); font-size: 14.5px; line-height: 1.6; color: ${C.textMuted}; }
  .fs-tl-later { border-top-style: dashed; }

  .fs-fig { background: ${C.bgAlt}; padding: 22px 0 10px; display: flex; justify-content: center; margin-bottom: 20px; }

  .fs-why { background: ${C.navy}; color: ${C.white}; }
  .fs-why .fs-h2 { color: ${C.white}; }
  .fs-why .fs-eyebrow { color: ${C.gold}; }
  .fs-why .fs-p { color: rgba(255,255,255,0.84); }
  .fs-why .fs-p a { color: ${C.white}; }

  .fs-faq details { border-top: 1px solid ${C.border}; }
  .fs-faq details:last-child { border-bottom: 1px solid ${C.border}; }
  .fs-faq summary { font-family: var(--head); font-size: clamp(21px, 2.6vw, 25px); font-weight: 600; color: ${C.navy}; line-height: 1.3; padding: 20px 40px 20px 0; cursor: pointer; list-style: none; position: relative; }
  .fs-faq summary::-webkit-details-marker { display: none; }
  .fs-faq summary::after { content: "+"; position: absolute; right: 6px; top: 16px; font-family: var(--body); font-size: 26px; font-weight: 300; color: ${C.goldDark}; }
  .fs-faq details[open] summary::after { content: "–"; }
  .fs-faq details p { font-family: var(--body); font-size: 16px; line-height: 1.8; color: ${C.textMuted}; padding: 0 40px 24px 0; }

  .fs-rel { display: grid; grid-template-columns: repeat(2, 1fr); gap: 1px; background: ${C.border}; border: 1px solid ${C.border}; }
  .fs-rel a { display: block; background: ${C.white}; padding: 22px 24px; text-decoration: none; transition: background 0.25s; }
  .fs-rel a:hover { background: #fffdf8; }
  .fs-rel a b { display: block; font-family: var(--head); font-size: 22px; font-weight: 600; color: ${C.navy}; line-height: 1.25; }
  .fs-rel a span { display: block; font-family: var(--body); font-size: 14.5px; color: ${C.textMuted}; margin-top: 4px; line-height: 1.55; }
  .fs-rel a.fs-rel-hub { grid-column: 1 / -1; background: ${C.navy}; }
  .fs-rel a.fs-rel-hub b { color: ${C.gold}; }
  .fs-rel a.fs-rel-hub span { color: rgba(255,255,255,0.78); }

  .fs-cta { background: ${C.bgAlt}; text-align: center; }
  .fs-cta .fs-rule { margin-left: auto; margin-right: auto; }
  .fs-cta-links { display: flex; gap: 12px; justify-content: center; flex-wrap: wrap; margin-top: 30px; }
  .fs-offices { font-family: var(--body); font-size: 14.5px; line-height: 1.7; color: ${C.textMuted}; margin-top: 26px; }
  .fs-offices a { color: ${C.navy}; font-weight: 600; }

  @media (max-width: 860px) {
    .fs-facts, .fs-facts.fs-facts-3 { grid-template-columns: 1fr 1fr; }
    .fs-fact:nth-child(3) { padding-left: 0; border-left: none; }
    .fs-fact:nth-child(n+3) { border-top: 1px solid rgba(255,255,255,0.14); }
    .fs-cards, .fs-cards.fs-cards-3, .fs-rel { grid-template-columns: 1fr; }
    .fs-hero .fs-eyebrow { font-size: 12px; letter-spacing: 2.5px; line-height: 1.6; }
    .fs-tl { grid-template-columns: 1fr; margin-top: 36px; }
    .fs-tl-step { border-top: none; border-left: 2px solid ${C.navy}; padding: 0 0 26px 24px; }
    .fs-tl-step:last-child { padding-bottom: 0; }
    .fs-tl-step::before { top: 4px; left: -7px; }
    .fs-tl-later { border-left-style: dashed; }
    .fs-step { grid-template-columns: 44px 1fr; gap: 12px; }
  }
  @media (prefers-reduced-motion: reduce) { html { scroll-behavior: auto; } }
`;

/* ─── Building blocks ─── */

export function Section({ id, eyebrow, title, alt, narrow, children, intro }) {
  return (
    <section id={id} className={`fs-sec${alt ? " fs-alt" : ""}`}>
      <div className="fs-wrap">
        <div className={narrow === false ? undefined : "fs-narrow"}>
          {eyebrow && <p className="fs-eyebrow">{eyebrow}</p>}
          <h2 className="fs-h2">{title}</h2>
          <GoldRule className="fs-rule" />
          {intro && intro.map((t, i) => <p key={i} className="fs-p" style={i === intro.length - 1 && children ? { marginBottom: "36px" } : undefined}>{t}</p>)}
        </div>
        {children}
      </div>
    </section>
  );
}

export function Cards({ items, three, wideFirst }) {
  return (
    <div className={`fs-cards${three ? " fs-cards-3" : ""}`}>
      {items.map((c, i) => (
        <div key={c.title} className={`fs-card${wideFirst && i === 0 ? " fs-wide" : ""}`}>
          <h3>{c.title}</h3>
          {c.plain && <p className="fs-plain">{c.plain}</p>}
          <p>{c.text}</p>
          {c.more && <p>{c.more}</p>}
          {c.points && <ul className="fs-list">{c.points.map((pt) => <li key={pt}>{pt}</li>)}</ul>}
        </div>
      ))}
    </div>
  );
}

export function Steps({ items }) {
  return (
    <div className="fs-steps">
      {items.map((s) => (
        <div key={s.title} className="fs-step"><div><h3>{s.title}</h3><p>{s.text}</p></div></div>
      ))}
    </div>
  );
}

export function Faq({ faqs, title = "Common questions" }) {
  return (
    <section id="faq" className="fs-sec">
      <div className="fs-wrap">
        <div className="fs-narrow">
          <p className="fs-eyebrow">Questions</p>
          <h2 className="fs-h2">{title}</h2>
          <GoldRule className="fs-rule" />
          <div className="fs-faq">
            {faqs.map((f) => <details key={f.q}><summary>{f.q}</summary><p>{f.a}</p></details>)}
          </div>
        </div>
      </div>
    </section>
  );
}

/* Links back to the main page and across to the other supporting pages. */
export function Related({ self, intro }) {
  const others = FFS_SUBPAGES.filter((p) => p.href !== self);
  return (
    <section id="related" className="fs-sec fs-alt">
      <div className="fs-wrap">
        <p className="fs-eyebrow">Facial feminization</p>
        <h2 className="fs-h2">The rest of the picture</h2>
        <GoldRule className="fs-rule" />
        <p className="fs-p fs-narrow" style={{ marginBottom: "32px" }}>
          {intro || "This page covers one part of facial feminization surgery."} The main page brings the procedures together, with before-and-after photos, and these pages go deeper on the others.
        </p>
        <div className="fs-rel">
          <a className="fs-rel-hub" href={FFS_HUB.href}><b>{FFS_HUB.label}: the full picture</b><span>Every procedure that can be part of a surgical plan, the incision options, staging, and before-and-after photos.</span></a>
          {others.map((p) => <a key={p.href} href={p.href}><b>{p.label}</b><span>{p.short}</span></a>)}
        </div>
      </div>
    </section>
  );
}

/* Navy "your surgeon" band. `children` are the paragraphs (use className="fs-p"). */
export function Why({ title, children }) {
  return (
    <section id="surgeon" className="fs-sec fs-why">
      <div className="fs-wrap">
        <div className="fs-narrow">
          <p className="fs-eyebrow">Your surgeon</p>
          <h2 className="fs-h2">{title}</h2>
          <GoldRule className="fs-rule" />
          {children}
        </div>
      </div>
    </section>
  );
}

export function Consult({ text }) {
  return (
    <section id="consult" className="fs-sec fs-cta">
      <div className="fs-wrap">
        <p className="fs-eyebrow">Next step</p>
        <h2 className="fs-h2">Start with a consultation</h2>
        <GoldRule className="fs-rule" align="center" />
        <p className="fs-p fs-narrow" style={{ margin: "0 auto" }}>
          {text || "Dr. Ovadia will examine your facial structure, go over which procedures make sense for you, and explain how your surgery and recovery would be planned."}
        </p>
        <div className="fs-cta-links">
          <a className="fs-btn fs-btn-gold" href={PHONE_HREF}>Call {PHONE_DISPLAY}</a>
          <a className="fs-btn fs-btn-navy" href="/#contact">Send a message</a>
        </div>
        <p className="fs-offices">
          225 Broadway, Suite 3100, New York, NY<br />
          377 Jersey Ave, Suite 440, Jersey City, NJ · 90 Millburn Ave, Suite 206, Millburn, NJ<br />
          Phone and video consultations available · <a href={`mailto:${EMAIL}`}>{EMAIL}</a>
        </p>
      </div>
    </section>
  );
}

/* ─── Page frame: hero with breadcrumb back to the main FFS page, key facts, jump links ─── */
export function FfsSubpage({ eyebrow, title, lede, facts, jumps, children, crumb, resultsHref, stripe = true }) {
  return (
    <main id="main-content" className="fs" style={{ background: C.bg }}>
      <style>{STYLES}</style>
      <header className="fs-hero">
        <div className="fs-wrap">
          <p className="fs-crumb"><a href={crumb ? crumb.href : FFS_HUB.href}>{crumb ? crumb.label : "Facial feminization surgery"}</a> &nbsp;/&nbsp; {eyebrow}</p>
          <h1>{title}</h1>
          {stripe ? (
            <div className="fs-stripe" aria-hidden="true">
              <span style={{ background: C.transBlue }} /><span style={{ background: C.transPink }} /><span style={{ background: C.white }} />
            </div>
          ) : <div className="fs-stripe" aria-hidden="true"><span style={{ background: C.gold }} /></div>}
          <p className="fs-hero-lede">{lede}</p>
          <div className="fs-btns">
            <a className="fs-btn fs-btn-gold" href="#consult">Request a consultation</a>
            <a className="fs-btn fs-btn-line" href={resultsHref || `${FFS_HUB.href}#results`}>See before and after</a>
          </div>
          {facts && (
            <div className={`fs-facts${facts.length === 3 ? " fs-facts-3" : ""}`}>
              {facts.map((f) => <div key={f.big} className="fs-fact"><b>{f.big}</b><span>{f.small}</span></div>)}
            </div>
          )}
        </div>
      </header>
      {jumps && (
        <nav className="fs-jump" aria-label="On this page">
          <div className="fs-wrap fs-jump-in">
            {jumps.map((j) => <a key={j.id} href={`#${j.id}`}>{j.label}</a>)}
          </div>
        </nav>
      )}
      {children}
    </main>
  );
}
